import { Box, Card, CardContent, Skeleton, Typography } from "@mui/material";
import { Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { LabelValue } from "@/features/dashboard/api/get-dashboard-data";

interface Props {
    title: string;
    data?: LabelValue[];
    loading: boolean;
    horizontal?: boolean;
    color?: string;
    currencySymbol?: string;
}

interface TooltipData { active?: boolean; payload?: { value?: number; name?: string }[]; label?: string; currencySymbol?: string; }
function CustomTooltip({ active, payload, label, currencySymbol }: TooltipData) {
    if (!active || !payload?.length) return null;
    const value = payload[0].value;
    const formatted = currencySymbol
        ? `${currencySymbol} ${value?.toLocaleString("es-PY")}`
        : value?.toLocaleString("es-PY");
    return (
        <Box sx={{
            bgcolor: "grey.900", color: "common.white",
            px: 1.5, py: 1, borderRadius: "8px",
            fontSize: "0.8125rem", fontWeight: 500,
            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        }}>
            <Box sx={{ color: "grey.400", fontSize: "0.75rem", mb: 0.25 }}>{label ?? payload[0].name}</Box>
            <Box>{formatted}</Box>
        </Box>
    );
}

function EmptyState() {
    return (
        <Box sx={{ height: 260, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body2" color="text.disabled">Sin datos para el período</Typography>
        </Box>
    );
}

export default function BarChartCard({ title, data, loading, horizontal, color = "#635bff", currencySymbol }: Props) {
    const isEmpty = !loading && (!data || data.length === 0);

    return (
        <Card sx={{ height: "100%" }}>
            <CardContent sx={{ pb: "16px !important" }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1.5 }}>
                    {title}
                </Typography>

                {loading ? (
                    <Skeleton variant="rectangular" height={260} sx={{ borderRadius: "8px" }} />
                ) : isEmpty ? (
                    <EmptyState />
                ) : (
                    <ResponsiveContainer width="100%" height={260}>
                        {horizontal ? (
                            <BarChart data={data} layout="vertical" margin={{ left: -10, right: 8, top: 4, bottom: 4 }}>
                                <XAxis
                                    type="number"
                                    tick={{ fontSize: 11, fill: "var(--mui-palette-text-secondary)" }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v: number) => currencySymbol ? `${currencySymbol} ${v.toLocaleString("es-PY")}` : v.toLocaleString("es-PY")}
                                />
                                <YAxis
                                    type="category"
                                    dataKey="label"
                                    width={140}
                                    tick={{ fontSize: 11, fill: "var(--mui-palette-text-primary)" }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v: string) => v.length > 18 ? `${v.slice(0, 17)}…` : v}
                                />
                                <Tooltip content={<CustomTooltip currencySymbol={currencySymbol} />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                                <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={22}>
                                    {data?.map((_, i) => (
                                        <Cell
                                            key={i}
                                            fill={color}
                                            fillOpacity={1 - i * 0.06}
                                        />
                                    ))}
                                </Bar>
                            </BarChart>
                        ) : (
                            <BarChart data={data} margin={{ left: 8, right: 8, top: currencySymbol ? 16 : 4, bottom: 4 }}>
                                <XAxis
                                    dataKey="label"
                                    tick={{ fontSize: 11, fill: "var(--mui-palette-text-secondary)" }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    width={currencySymbol ? 80 : 35}
                                    tick={{ fontSize: 11, fill: "var(--mui-palette-text-secondary)" }}
                                    axisLine={false}
                                    tickLine={false}
                                    tickFormatter={(v: number) => currencySymbol ? `${currencySymbol} ${v.toLocaleString("es-PY")}` : v.toLocaleString("es-PY")}
                                />
                                <Tooltip content={<CustomTooltip currencySymbol={currencySymbol} />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                                <Bar dataKey="value" radius={[4, 4, 0, 0]} fill={color} maxBarSize={40} />
                            </BarChart>
                        )}
                    </ResponsiveContainer>
                )}
            </CardContent>
        </Card>
    );
}
